<template>
  <div class="relative">
    <!-- Select Component -->
    <Select v-model="selectedEmployers" multiple>
      <SelectTrigger class="w-full text-sm bg-white text-black">
        <SelectValue :placeholder="placeholder" />
      </SelectTrigger>

      <SelectContent>
        <!-- Search Input (Fixed at top) -->
        <div class="fixed top-0 left-0 w-full px-2 py-1.5 mb-2 z-10 bg-white">
          <input
            v-model="employerSearch"
            type="text"
            placeholder="Search employers..."
            class="relative w-full px-2 py-1 pr-5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
            @keydown.stop
            @keyup.stop
            @keypress.stop
          />
          <X
            class="absolute top-3 right-3 text-gray-500 h-4 w-4 cursor-pointer"
            @click="clearEmployerSearch"
          />
        </div>
        
        <!-- Employer Options (Scrollable) -->
        <div 
          ref="employerOptionsContainer"
          class="mt-8 max-h-[200px] overflow-y-auto" 
          @scroll="handleEmployerScroll"
        >
          <!-- Loading indicator -->
          <div v-if="employerStore.isLoading" class="flex justify-center py-2">
            <div class="animate-spin rounded-full h-4 w-4 border-2 border-primary"></div>
          </div>
          
          <!-- Employer options -->
          <SelectItem 
            v-for="employer in filteredEmployers" 
            :key="employer.id" 
            :value="employer"
          >
            {{ employer.name }}
          </SelectItem>
          
          <!-- Load more indicator -->
          <div v-if="employerStore.nextPage && !employerStore.isLoading" class="text-center text-sm text-gray-500 py-2">
            Scroll for more...
          </div>
        </div>
      </SelectContent>
    </Select>
    
    <!-- Selected Employers Display -->
    <div v-if="selectedEmployers.length > 0" class="mt-2 flex flex-wrap gap-2">
      <span 
        v-for="employer in selectedEmployers" 
        :key="employer.id" 
        class="flex items-center w-fit text-xs bg-cosmicPurple/30 text-gray-800 rounded-md px-2 py-1"
      >
        {{ employer.name }}
        <X 
          class="inline ml-2 text-gray-500 h-3 w-3 cursor-pointer" 
          @click="removeEmployer(employer)" 
        />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { X } from 'lucide-vue-next'
import { useEmployerStore } from '@/stores/employer'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

interface Employer {
  id: number
  name: string
  logo?: string | null
}

interface Props {
  modelValue: number[]
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select employers...'
})

const emit = defineEmits<{
  'update:modelValue': [value: number[]]
}>()

const employerStore = useEmployerStore()
const employerSearch = ref('')
const employerOptionsContainer = ref<HTMLElement | null>(null)

// Convert model value (array of IDs) to selected employers objects
const selectedEmployers = computed({
  get: () => {
    return props.modelValue.map(id => 
      employerStore.employerOptions.find(employer => employer.id === id)
    ).filter(Boolean) as Employer[]
  },
  set: (newEmployers: Employer[]) => {
    emit('update:modelValue', newEmployers.map(emp => emp.id))
  }
})

// Filtered employers for the dropdown
const filteredEmployers = computed(() => {
  return employerStore.employerOptions
})

// Clear search input
const clearEmployerSearch = () => {
  employerSearch.value = ''
}

// Remove an employer from selection
const removeEmployer = (employer: Employer) => {
  const newEmployers = selectedEmployers.value.filter(e => e.id !== employer.id)
  selectedEmployers.value = newEmployers
}

// Handle infinite scroll for employer options
const handleEmployerScroll = async (event: Event) => {
  const container = event.target as HTMLElement
  const { scrollTop, scrollHeight, clientHeight } = container
  
  // Check if we're near the bottom (within 50px)
  if (scrollHeight - (scrollTop + clientHeight) < 50) {
    await employerStore.loadMoreEmployers()
  }
}

// Watch for search changes to trigger API search
watch(employerSearch, async (newValue) => {
  await employerStore.searchEmployers(newValue)
})

// Initialize employer data on mount
onMounted(async () => {
  await employerStore.searchEmployers('') // Initial load with empty search
})
</script>